import React, { Component } from 'react';
import { isEqual, get } from 'lodash';
import PropTypes from 'prop-types';
import memoize from 'fast-memoize';
import helpers from 'helpers';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import CellDivWithLabel from 'components/molecules/tables/components/CellDivWithLabel';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { generateFooterData, generateHeaders, applyDefaultColumns } from 'helpers/tables';
import { getSocialActionTypeLabel } from 'helpers/charts';
import { capitalizeFirstLetter } from 'helpers/strings';
import RenderingTile from 'components/molecules/RenderingTile';
import DefaultTable from 'components/atoms/tables/smarttables/DefaultTable';

// Add grouped_type for filtering
const ACCESSORS = ['platform', 'action_type', 'actions', 'is_ecpa'];

const createTableData = memoize(({ data = {}, props }) => {
    let finalData = [];
    const { accessors = ACCESSORS, totalCampaignSpend, conversionTermSingular } = props;

    if (data.hasData) {
        const { actionTypeMap } = data;
        finalData = Object.entries(actionTypeMap).map(c => ({
            name: c[1].name,
            ...data[c[0]],
            cost_per_action: helpers.safeDivide(totalCampaignSpend, data[c[0]].actions),
        }));
    }


    const headers = generateHeaders({
        keys: ['actions', 'is_ecpa'],
        footerData: generateFooterData({
            data: finalData.filter(d => !d.grouped_type), // we do not want to include grouped action types
            filteredKeys: ['name', 'action_type', 'is_ecpa'],
            extraData: { totalCampaignSpend }
        }),
        initial: {
            platform: {
                Header: 'Platform',
                accessor: 'platform',
                Cell: row => <CellDiv value={capitalizeFirstLetter(row.value)} />,
                Footer: (
                    <CellDiv value='' />
                ),
                filter: 'textColumnFilter',
                isIndexCol: false
            },
            action_type: {
                Header: 'Action Type',
                accessor: 'action_type',
                Cell: ({ row: { original } }) => (
                    <CellDivWithLabel
                        value={original.action_type}
                        labelMeta={getSocialActionTypeLabel(original, { conversionTermSingular })}
                    />
                ),
                filter: 'textColumnFilter',
                isIndexCol: true
            }
        },
        colOptions: { labelCode: conversionTermSingular }
    });
    const columns = applyDefaultColumns(
        accessors.map(accessor => headers[accessor]),
        ['action_type', 'actions']
    );

    return { data: finalData, columns };
});

class SocialActionOverviewTable extends Component {
    constructor(props) {
        super(props);

        const showAllActions = false;
        const totalCampaignSpend = get(this.props.campaigns, 'statistics.spend', 0);

        const { data, columns } = createTableData({
            data: this.props.actions,
            props: { ...props, totalCampaignSpend },
        });
        this.state = {
            data,
            columns,
            showAllActions,
            exportName: props.exportName,
            actions: this.props.actions,
            totalCampaignSpend: this.props.totalCampaignSpend
        };

        this.thProps = this.thProps.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props)
            || !isEqual(nextState, this.state);
    }

    thProps(state, rowInfo, column) {
        return {
            style: { outline: 0, textTransform: 'uppercase' },
            ...(column.tooltip && { title: column.tooltip })
        };
    }

    static getDerivedStateFromProps(props, state) {
        const nextActions = props.actions || {};
        const nextCampaigns = props.campaigns || {};
        const nextExportName = props.exportName;
        let nextState = null;

        const totalCampaignSpend = get(nextCampaigns, 'statistics.spend', 0);
        const { data, columns } = createTableData({
            data: nextActions,
            props: { ...props, totalCampaignSpend },
        });

        nextState = {
            ...state,
            columns,
            data,
            exportName: nextExportName,
            actions: nextActions,
            totalCampaignSpend: props.totalCampaignSpend
        };
        return nextState;
    }

    render() {
        const { data, actions, exportName, columns, showAllActions } = this.state;
        const title = 'Action Overview';
        const hasActionsData = get(actions, 'hasData', null);
        const { cacheKey, conversionTermSingular, conversionTermPlural } = this.props;

        return (hasActionsData ?
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'} allowExport={false}>{title}</ContentHeader>
                <DefaultTable
                    columns={columns}
                    data={data}
                    cacheKey={cacheKey}
                    globalSearch={false}
                    exportData
                    fileName={exportName}
                    exportName={exportName}
                    title={title}
                    conversionTermSingular={conversionTermSingular}
                    pagination={data.length > RowsPerPageDefault}
                    dataToggle={{
                        label: `Show Only ${conversionTermPlural}`,
                        initialFilter: !showAllActions,
                    }}
                    aggregate={true}
                    defaultState={{
                        pageSize: RowsPerPageDefault,
                        sortBy: [
                            {
                                id: 'actions',
                                desc: true
                            }
                        ]
                    }}
                />
            </ContentTile>
            :
            <RenderingTile height='400px' show={hasActionsData} />
        );
    }
}

SocialActionOverviewTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    actions: PropTypes.shape({
        hasData: PropTypes.bool,
        actionTypeMap: PropTypes.object,
        params: PropTypes.object
    }).isRequired,
    campaigns: PropTypes.shape({
        statistics: PropTypes.object
    }).isRequired,
    showAllActions: PropTypes.bool,
    totalCampaignSpend: PropTypes.number
};

export default SocialActionOverviewTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/SocialActionOverviewTable.js